import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { IndianRupee, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function PaymentDialog({ open, onOpenChange, task, onPaid }) {
  const [config, setConfig] = useState({ configured: false, key_id: null });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
    setAmount("");
  }, [open]);

  const start = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    // Ensure the Razorpay checkout script is present; inject if missing.
    if (!window.Razorpay) {
      await new Promise((resolve, reject) => {
        const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
        if (existing) { existing.addEventListener("load", resolve); existing.addEventListener("error", reject); return; }
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = resolve; s.onerror = reject;
        document.body.appendChild(s);
      }).catch(() => {});
    }
    if (!window.Razorpay) { toast.error("Razorpay checkout script failed to load"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/order", {
        task_id: task.id,
        amount_inr: amt,
        description: `${task.service} — ${task.client_name}`,
      });
      const opts = {
        key: data.key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: "Mind Maker 369",
        description: data.description,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: "#09090b" },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment received", { description: `${task.client_name} · ₹${amt.toFixed(2)}` });
            onPaid?.();
            onOpenChange(false);
          } catch (e) {
            toast.error("Verification failed", { description: e.response?.data?.detail || e.message });
          }
        },
        modal: {
          ondismiss: () => setBusy(false),
        },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed", { description: resp?.error?.description || "Client cancelled or bank declined" });
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      toast.error("Could not start checkout", { description: e.response?.data?.detail || e.message });
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-2xl">Collect Payment</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Raise a Razorpay invoice against this task. On success the task flips to Paid automatically.</DialogDescription>
        </DialogHeader>

        {task ? (
          <div className="border border-zinc-200 bg-zinc-50 p-3 text-sm">
            <div className="text-[10px] tracking-[0.16em] uppercase font-semibold text-zinc-500">Task</div>
            <div className="mt-1 font-semibold">{task.client_name}</div>
            <div className="text-zinc-600 text-xs">{task.service}</div>
          </div>
        ) : null}

        {!config.configured ? (
          <div className="border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex gap-2 items-start" data-testid="razorpay-not-configured">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Razorpay isn&apos;t configured yet.</div>
              <div className="mt-1">Add <code className="font-mono">RAZORPAY_KEY_ID</code> and <code className="font-mono">RAZORPAY_KEY_SECRET</code> to <code className="font-mono">/app/backend/.env</code> and restart the backend. Then reopen this dialog.</div>
            </div>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label className="text-[11px] tracking-[0.14em] uppercase font-semibold">Amount (INR)</Label>
          <div className="relative">
            <IndianRupee size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="payment-amount-input"
              type="number"
              step="0.01"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 2500"
              className="pl-8"
              disabled={!config.configured}
            />
          </div>
          <div className="text-[11px] text-zinc-500">Client sees this in the Razorpay checkout. All amounts in INR.</div>
        </div>

        <div className="flex items-center gap-2 text-[11px] text-zinc-500 border-t border-zinc-200 pt-3">
          <ShieldCheck size={14} className="text-emerald-700" />
          <span>Signature is verified server-side before the task is marked paid.</span>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid="payment-start-btn"
            onClick={start}
            disabled={busy || !config.configured || !amount}
            className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none"
          >
            {busy ? "Opening checkout…" : "Open Razorpay Checkout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
